import { immutableSplice } from 'helpers/array'
import makeRemoveOnGraphQLItemDelete from './removeOnGQLItemDeletion'
import makeAddAsFirstNode from './addAsFirstNode'

export const getEmptyListState = (name, { pristine = true } = {}) => ({
  name,
  loading: false,
  pristine,
  error: null,
  totalCount: null,
  nodes: [],
  edges: [],
  pageInfo: {
    hasNextPage: true,
    endCursor: null,
  },
  settings: null,
})

// Selectors
export const getListState = listName => state => {
  return state[listName] || getEmptyListState(listName)
}

// Actions
export function fetchPageStart(listName, settings, options = {}) {
  return {
    type: 'PAGINATION/FETCH_START',
    payload: { listName, settings, options },
  }
}

export function fetchPageError(listName, { error, meta }) {
  return {
    type: 'PAGINATION/FETCH_ERROR',
    payload: {
      listName,
      error,
    },
    meta,
  }
}

export function fetchPageSuccess(listName, page, options = {}) {
  return {
    type: 'PAGINATION/FETCH_SUCCESS',
    payload: {
      listName,
      page,
      options,
    },
  }
}

export function resetList(listName) {
  return {
    type: 'PAGINATION/RESET_LIST',
    payload: {
      listName,
    },
  }
}

export function itemInsertions(listName, insertions) {
  return {
    type: 'PAGINATION/ITEM_INSERTION',
    payload: {
      listName,
      insertions,
    },
  }
}

// Reducer
export function makeReducer(extraReducers = []) {
  const runExtraReducers = (state, action) =>
    extraReducers.reduce((state, reducer) => reducer(state, action), state)

  return function reducer(state = {}, action) {
    const { type, payload, metadata } = action
    if (metadata && metadata.pagination) {
      state = reducer(state, metadata.pagination)
      state = runExtraReducers(state, metadata.pagination)
    }

    state = runExtraReducers(state, action)

    switch (type) {
      case 'PAGINATION/FETCH_START': {
        const { listName, settings, options = {} } = payload
        const oldListState = options.startFresh
          ? getEmptyListState(listName, { pristine: false })
          : getListState(listName)(state)

        const newListState = {
          ...oldListState,
          settings,
          loading: true,
          error: null,
        }
        return {
          ...state,
          [listName]: newListState,
        }
      }
      case 'PAGINATION/FETCH_ERROR': {
        const { listName, error } = payload
        const oldListState = getListState(listName)(state)
        const newListState = {
          ...oldListState,
          pristine: false,
          loading: false,
          error,
        }
        return {
          ...state,
          [listName]: newListState,
        }
      }
      case 'PAGINATION/RESET_LIST': {
        const { listName } = payload
        const stateClone = { ...state }
        delete stateClone[listName]
        return stateClone
      }
      case 'PAGINATION/FETCH_SUCCESS': {
        const { listName, page, options } = payload
        const oldListState = getListState(listName)(state)
        const listToExtend = options.startFresh
          ? getEmptyListState(listName, { pristine: false })
          : oldListState
        const newListState = mergePage(
          {
            ...listToExtend,
            pristine: false,
            settings: oldListState.settings,
          }, // keep the list settings
          page
        )
        return {
          ...state,
          [listName]: newListState,
        }
      }
      case 'PAGINATION/ITEM_INSERTION': {
        const { listName, insertions } = payload
        const oldListState = getListState(listName)(state)

        const newListState = insertions.reduce((listState, insertion) => {
          return insertEdgeIntoPage(listState, insertion)
        }, oldListState)

        if (newListState === oldListState) {
          return state
        }

        return {
          ...state,
          [listName]: newListState,
        }
      }
      default:
        return state
    }
  }
}

// Utils
const edgesToNodes = edges => edges.map(({ node }) => node.id)

const mergePage = (listState, page) => {
  const { pageInfo, edges, totalCount } = page
  const nodes = edgesToNodes(edges)

  return {
    ...listState,
    loading: false,
    error: null,
    totalCount,
    nodes: [...listState.nodes, ...nodes],
    edges: [...listState.edges, ...edges],
    pageInfo,
  }
}

const insertEdgeIntoPageBefore = (listState, edge, before) => {
  const { edges } = listState
  const edgeBefore = edges.filter(({ node }) => node.id === before)[0]

  if (!edgeBefore) {
    return false
  }

  const edgeIndex = edges.indexOf(edgeBefore)
  const newEdges = immutableSplice(edges, edgeIndex, 0, edge)

  return {
    ...listState,
    totalCount: listState.totalCount + 1,
    edges: newEdges,
    nodes: edgesToNodes(newEdges),
  }
}

const insertEdgeIntoPageAfter = (listState, edge, after) => {
  const { edges } = listState
  const edgeAfter = edges.filter(({ node }) => node.id === after)[0]

  if (!edgeAfter) {
    return false
  }

  const edgeIndex = edges.indexOf(edgeAfter)
  const newEdges = immutableSplice(edges, edgeIndex + 1, 0, edge)

  return {
    ...listState,
    totalCount: listState.totalCount + 1,
    edges: newEdges,
    nodes: edgesToNodes(newEdges),
  }
}

const insertEdgeIntoPage = (listState, { edge, before, after }) => {
  if (before) {
    const newListState = insertEdgeIntoPageBefore(listState, edge, before)
    if (newListState) {
      return newListState
    }
  } else {
    if (after) {
      const newListState = insertEdgeIntoPageAfter(listState, edge, after)
      if (newListState) {
        return newListState
      }
    }
  }
  return listState
}

export default makeReducer([
  // remove from all notes and channels when notes are archived
  makeRemoveOnGraphQLItemDelete(
    listName => listName !== 'allArchivedNotes',
    'ARCHIVED_NOTES'
  ),
  // remove from trash when note is deleted
  makeRemoveOnGraphQLItemDelete(
    listName => listName === 'allArchivedNotes',
    'DELETED_NOTES'
  ),
  makeRemoveOnGraphQLItemDelete(
    listName => listName === 'allArchivedNotes',
    'UNARCHIVED_NOTES'
  ),

  makeAddAsFirstNode(
    listName => listName === 'allTemplateNotes',
    'noteTemplateCreated'
  ),

  // remove from templates when note is deleted
  makeRemoveOnGraphQLItemDelete(
    listName => listName === 'allTemplateNotes',
    'noteTemplateDeleted'
  ),

  // add as first node when note id created and channel matches
  makeAddAsFirstNode(allNotesOrMatchingChannel, 'noteCreated'),

  // add as first node when note is moved to the channel
  // makeAddAsFirstNode(matchingChannel, 'noteMoved'),

  // try to remove from channel when note is removed to another channel
  makeRemoveOnGraphQLItemDelete(
    (...args) => !matchAllNotes(...args) && !matchingChannel(...args),
    'MOVED_NOTES_TO'
  ),
])

function matchingChannel(listName, list, node) {
  // look for matching channel
  return list.settings && list.settings.channelId === node.channelId
}

function matchAllNotes(listName) {
  return listName === 'allNotes'
}

function allNotesOrMatchingChannel(listName, list, node) {
  return (
    matchAllNotes(listName, list, node) || matchingChannel(listName, list, node)
  )
}
